'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type TeamMemberWithUserDto,
  TeamsApi,
  type WorkspaceMemberWithUserDto,
} from '@up2stay/up2promo-api-client';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Checkbox,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Label,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  Skeleton,
} from '@up2stay/up2promo-ui';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { invalidateUserTeamMembershipQueries } from '@/core/hooks/use-user-team-membership';
import {
  triggerSelectionHaptic,
  triggerTapHaptic,
} from '@/core/lib/dnd-haptics';
import {
  isActiveWorkspaceMember,
  workspaceMemberAvatarUrl,
  workspaceMemberDisplayName,
  workspaceMemberInitials,
  workspaceMemberSecondaryLine,
} from '@/core/lib/workspace-member-display';
import { fetchAllWorkspaceMembersPages } from '@/core/lib/workspace-members-fetch-all';
import { useLocale } from '@/core/providers/dictionary-provider';

interface AddTeamMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  teamId: string;
  /** Members already part of this team – will be filtered out of the list. */
  currentTeamMembers: TeamMemberWithUserDto[];
  onSuccess: () => void;
  dict: {
    title: string;
    description: string;
    memberLabel: string;
    searchPlaceholder: string;
    noAvailable: string;
    noResults: string;
    add: string;
    addWithCount: string;
    adding: string;
    cancel: string;
    success: string;
    successPlural: string;
    error: string;
    errorPartial: string;
    errorAlreadyInTeam: string;
    selectedCount: string;
  };
}

function MemberRowContent({ member }: { member: WorkspaceMemberWithUserDto }) {
  const primary = workspaceMemberDisplayName(member);
  const secondary = workspaceMemberSecondaryLine(member);
  const src = workspaceMemberAvatarUrl(member);
  const initials = workspaceMemberInitials(member);

  return (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <Avatar className="h-9 w-9 shrink-0">
        {src ? <AvatarImage src={src} alt="" /> : null}
        <AvatarFallback className="text-xs font-medium">
          {initials}
        </AvatarFallback>
      </Avatar>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{primary}</span>
        {secondary ? (
          <span className="block truncate text-xs text-muted-foreground">
            {secondary}
          </span>
        ) : null}
      </span>
    </div>
  );
}

const EMPTY_MEMBER_IDS = new Set<string>();
const EMPTY_WORKSPACE_MEMBERS: WorkspaceMemberWithUserDto[] = [];

async function fetchAssignedWorkspaceMemberIds(
  workspaceId: string,
): Promise<Set<string>> {
  const teamsRes = await TeamsApi.findAll({
    path: { workspaceId },
    query: { page: 1, limit: 100 },
  });
  const teams = teamsRes.data?.data ?? [];
  const memberLists = await Promise.all(
    teams.map((team) =>
      TeamsApi.listMembers({
        path: { workspaceId, teamId: team.id },
      }),
    ),
  );
  const assigned = new Set<string>();
  for (const list of memberLists) {
    for (const tm of list.data ?? []) {
      assigned.add(tm.workspaceMemberId);
    }
  }
  return assigned;
}

function memberMatchesSearch(
  member: WorkspaceMemberWithUserDto,
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const name = workspaceMemberDisplayName(member).toLowerCase();
  const email = member.user?.email?.toLowerCase() ?? '';
  const username = member.user?.username?.toLowerCase() ?? '';
  return name.includes(q) || email.includes(q) || username.includes(q);
}

function getAddMemberErrorCode(err: unknown): string | undefined {
  const errors = (
    err as {
      response?: { data?: { errors?: Record<string, string> } };
    }
  )?.response?.data?.errors;
  return errors?.workspaceMemberId;
}

export function addTeamMemberPickerQueryKey(workspaceId: string) {
  return ['add-team-member-picker', workspaceId] as const;
}

export function AddTeamMemberDialog({
  open,
  onOpenChange,
  workspaceId,
  teamId,
  currentTeamMembers,
  onSuccess,
  dict,
}: AddTeamMemberDialogProps) {
  const locale = useLocale();
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [search, setSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const pickerDataQuery = useQuery({
    queryKey: addTeamMemberPickerQueryKey(workspaceId),
    queryFn: async () => {
      const [members, assignedInAnyTeamIds] = await Promise.all([
        fetchAllWorkspaceMembersPages(workspaceId),
        fetchAssignedWorkspaceMemberIds(workspaceId),
      ]);
      return { members, assignedInAnyTeamIds };
    },
    enabled: open && !!workspaceId,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: 1,
  });

  const workspaceMembers =
    pickerDataQuery.data?.members ?? EMPTY_WORKSPACE_MEMBERS;
  const assignedInAnyTeamIds =
    pickerDataQuery.data?.assignedInAnyTeamIds ?? EMPTY_MEMBER_IDS;
  const isMembersError = pickerDataQuery.isError;

  const [prevOpen, setPrevOpen] = useState(open);
  if (!open && open !== prevOpen) {
    setPrevOpen(open);
    setSearch('');
    setSelectedIds(new Set());
  } else if (open && open !== prevOpen) {
    setPrevOpen(open);
  }

  const toggleMember = (memberId: string) => {
    void triggerSelectionHaptic();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) {
        next.delete(memberId);
      } else {
        next.add(memberId);
      }
      return next;
    });
  };

  const currentTeamMemberIds = useMemo(
    () => new Set(currentTeamMembers.map((tm) => tm.workspaceMemberId)),
    [currentTeamMembers],
  );

  const availableMembers = useMemo(() => {
    const collator = locale === 'de' ? 'de' : 'en';
    return workspaceMembers
      .filter(isActiveWorkspaceMember)
      .filter(
        (m) =>
          !currentTeamMemberIds.has(m.id) && !assignedInAnyTeamIds.has(m.id),
      )
      .filter((m) => memberMatchesSearch(m, search))
      .sort((a, b) =>
        workspaceMemberDisplayName(a).localeCompare(
          workspaceMemberDisplayName(b),
          collator,
          { sensitivity: 'base' },
        ),
      );
  }, [
    assignedInAnyTeamIds,
    currentTeamMemberIds,
    locale,
    search,
    workspaceMembers,
  ]);

  const isLoadingList =
    pickerDataQuery.isLoading || (pickerDataQuery.isFetching && open);

  const emptyMessage = search.trim() ? dict.noResults : dict.noAvailable;

  const handleSubmit = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setIsSubmitting(true);
    try {
      const results = await Promise.allSettled(
        ids.map((workspaceMemberId) =>
          TeamsApi.addMember({
            path: { workspaceId, teamId },
            body: { workspaceMemberId },
          }),
        ),
      );

      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - succeeded;

      if (succeeded === ids.length) {
        toast.success(
          succeeded === 1
            ? dict.success
            : dict.successPlural.replace('{count}', String(succeeded)),
        );
        setSelectedIds(new Set());
        setSearch('');
        void queryClient.invalidateQueries({
          queryKey: addTeamMemberPickerQueryKey(workspaceId),
        });
        invalidateUserTeamMembershipQueries(queryClient, workspaceId);
        onOpenChange(false);
        onSuccess();
        return;
      }

      if (succeeded > 0) {
        toast.warning(
          dict.errorPartial
            .replace('{added}', String(succeeded))
            .replace('{failed}', String(failed)),
        );
        void queryClient.invalidateQueries({
          queryKey: addTeamMemberPickerQueryKey(workspaceId),
        });
        invalidateUserTeamMembershipQueries(queryClient, workspaceId);
        onSuccess();
        setSelectedIds(new Set());
        return;
      }

      const firstError = results.find((r) => r.status === 'rejected');
      if (
        firstError?.status === 'rejected' &&
        getAddMemberErrorCode(firstError.reason) === 'alreadyInAnotherTeam'
      ) {
        toast.error(dict.errorAlreadyInTeam);
      } else {
        toast.error(dict.error);
      }
    } catch (err) {
      console.error('Failed to add team members:', err);
      toast.error(dict.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedCount = selectedIds.size;
  const addButtonLabel = isSubmitting
    ? dict.adding
    : selectedCount > 0
      ? dict.addWithCount.replace('{count}', String(selectedCount))
      : dict.add;

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(next) => {
        void triggerTapHaptic();
        onOpenChange(next);
      }}
    >
      <ResponsiveDialogContent className="sm:max-w-[520px]">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>{dict.title}</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            {dict.description}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label>{dict.memberLabel}</Label>
            {selectedCount > 0 ? (
              <span className="text-xs text-muted-foreground">
                {dict.selectedCount.replace('{count}', String(selectedCount))}
              </span>
            ) : null}
          </div>
          <Command
            shouldFilter={false}
            className="overflow-hidden rounded-lg border"
          >
            <CommandInput
              placeholder={dict.searchPlaceholder}
              value={search}
              onValueChange={setSearch}
              disabled={isSubmitting}
            />
            <CommandList className="max-h-[min(50vh,300px)]">
              {isMembersError ? (
                <div className="py-8 text-center text-sm text-destructive">
                  {dict.error}
                </div>
              ) : null}
              {isLoadingList ? (
                <div className="space-y-2 p-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : null}
              {!isMembersError &&
              !isLoadingList &&
              availableMembers.length === 0 ? (
                <CommandEmpty className="py-8 text-sm text-muted-foreground">
                  {emptyMessage}
                </CommandEmpty>
              ) : null}
              {!isMembersError &&
              !isLoadingList &&
              availableMembers.length > 0 ? (
                <CommandGroup className="p-1">
                  {availableMembers.map((member) => {
                    const isSelected = selectedIds.has(member.id);
                    return (
                      <CommandItem
                        key={member.id}
                        value={`${member.id}-${workspaceMemberDisplayName(member)}`}
                        onSelect={() => toggleMember(member.id)}
                        disabled={isSubmitting}
                        className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2"
                      >
                        <Checkbox
                          checked={isSelected}
                          className="pointer-events-none"
                          tabIndex={-1}
                          aria-hidden
                        />
                        <MemberRowContent member={member} />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ) : null}
            </CommandList>
          </Command>
        </div>

        <ResponsiveDialogFooter className="mt-4 gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            {dict.cancel}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || selectedCount === 0}
          >
            {addButtonLabel}
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
